import { makeStyles, TextField, Typography } from "@material-ui/core";
import { Autocomplete, createFilterOptions } from "@material-ui/lab";
import React, { useMemo } from "react";
import { useSysadminData } from "../SysadminData";

const useStyles = makeStyles({
    option: {
        display: "flex",
        flexDirection: "column",
    },
});

interface Option {
    id: string;
    eaterGroup: string;
    office: string;
    officeId: string;
    company: string;
    companyId: string;
    members: number;
}

export interface Placement {
    companyId: string;
    officeId: string;
    eaterGroupId: string;
}

interface Props {
    value: string;
    onChange: (placement: Placement) => void;
    disabled?: boolean;
    /** Allow removing the placement. */
    clearable?: boolean;
    label?: string;
    size?: "small" | "medium";
    helperText?: React.ReactNode;
    error?: boolean;
    autoFocus?: boolean;
}

// Matches when every word is found in the eater group, office or company name
const filterOptions = createFilterOptions<Option>({ stringify: o => `${o.eaterGroup} ${o.office} ${o.company}` });
const filterAllWords = (options: Option[], state: { inputValue: string; getOptionLabel: (o: Option) => string }) => {
    const words = state.inputValue.trim().split(/\s+/).filter(Boolean);
    if (words.length <= 1) return filterOptions(options, state);
    return words.reduce((remaining, word) => filterOptions(remaining, { ...state, inputValue: word }), options);
};

/**
 * One search field for placing a user: finds eater groups by eater group, office or company name.
 * Only the selected company's eater groups are offered (plus the current value), grouped by office.
 */
export const EaterGroupSearch: React.FC<Props> = ({ value, onChange, disabled, clearable, label = "Eater group", size, helperText, error, autoFocus }) => {
    const classes = useStyles();
    const data = useSysadminData();

    const allOptions: Option[] = useMemo(() => {
        // One placement per email, as in buildAccounts
        const placements = new Map(data.users.map(u => [u.content.applicationUserId, u.content.eaterGroupId]));
        const members = new Map<string, number>();
        placements.forEach(id => id && members.set(id, (members.get(id) ?? 0) + 1));
        return data.eaterGroups
            .map(({ content }) => {
                const office = data.officeById(content.officeId);
                return {
                    id: content.id,
                    eaterGroup: content.eaterGroupName || "Unnamed eater group",
                    office: office?.officeName || "Unknown office",
                    officeId: content.officeId,
                    company: data.companyById(office?.companyId)?.companyName || "Unknown company",
                    companyId: office?.companyId ?? "",
                    members: members.get(content.id) ?? 0,
                };
            })
            .sort((a, b) => a.company.localeCompare(b.company) || a.office.localeCompare(b.office) || a.eaterGroup.localeCompare(b.eaterGroup));
    }, [data]);
    const companySelected = !!data.selectedCompanyId;
    const options = allOptions.filter(o => data.matchesSelectedCompany(o.companyId) || o.id === value);

    const selected = options.find(o => o.id === value) ?? null;

    return (
        <Autocomplete
            options={options}
            value={selected}
            disabled={disabled}
            disableClearable={!clearable}
            groupBy={o => (companySelected && data.matchesSelectedCompany(o.companyId) ? o.office : `${o.company} › ${o.office}`)}
            getOptionLabel={o => `${o.eaterGroup} – ${o.office}`}
            getOptionSelected={(o, v) => o.id === v.id}
            filterOptions={filterAllWords}
            onChange={(_, option) => onChange(option
                ? { eaterGroupId: option.id, officeId: option.officeId, companyId: data.companyIdOfOffice(option.officeId) ?? "" }
                : { eaterGroupId: "", officeId: "", companyId: "" })}
            renderOption={o => (
                <div className={classes.option}>
                    <span>{o.eaterGroup}</span>
                    <Typography variant="caption" color="textSecondary">{o.members === 1 ? "1 member" : `${o.members} members`}</Typography>
                </div>
            )}
            noOptionsText={companySelected ? `No eater groups in ${data.companyById(data.selectedCompanyId)?.companyName ?? "the selected company"} match` : "No eater groups match"}
            renderInput={params => (
                <TextField
                    {...params}
                    autoFocus={autoFocus}
                    variant="outlined"
                    size={size}
                    label={label}
                    placeholder={companySelected ? "Search eater group or office" : "Search eater group, office or company"}
                    helperText={helperText ?? (selected ? `${selected.company} › ${selected.office}` : undefined)}
                    error={error}
                />
            )}
        />
    );
};
